import { _isNil } from '@/littledash';
import { createContext, type FC, type ReactNode, useCallback, useContext, useMemo, useSyncExternalStore } from 'react';
import type { GraphSnapshot, LoadStatus } from './GraphCanvas.model';
import type { GraphCanvasEngine } from './GraphCanvasEngine';

/** The typed provider + hooks bundle produced by `createGraphCanvasContext` for one `TData`/`TMeta` pair. */
export interface GraphCanvasContext<TData extends Record<string, unknown>, TMeta extends Record<string, unknown>> {
  GraphCanvasProvider: FC<{ store: GraphCanvasEngine<TData, TMeta>; children: ReactNode }>;
  useGraphCanvasGraph: () => GraphSnapshot<TData>;
  useGraphCanvasMeta: () => Readonly<TMeta> | null;
  useGraphCanvasLoadStatus: () => LoadStatus;
}

/**
 * Creates a fully-typed provider + hooks bundle bound to a concrete `GraphCanvasEngine<TData, TMeta>`.
 * Each consumer instantiates this once with its own node-data and metadata types, then destructures
 * the provider and hooks — so the store's generics survive end-to-end with no `any` erasure and no
 * per-call-site type arguments.
 *
 * Modelled on the `StepFormEngine` React bridge (context holds a stable `{ store }`; hooks wrap
 * `useSyncExternalStore` with a memoised subscribe and read the getter by reference), generalised so
 * the same machinery serves any graph domain.
 */
export const createGraphCanvasContext = <
  TData extends Record<string, unknown>,
  TMeta extends Record<string, unknown>,
>(): GraphCanvasContext<TData, TMeta> => {
  type Store = GraphCanvasEngine<TData, TMeta>;
  const Context = createContext<{ store: Store } | null>(null);

  const useStore = (): Store => {
    const context = useContext(Context);
    if (_isNil(context)) {
      throw new Error('GraphCanvas hooks must be used within its GraphCanvasProvider');
    }
    return context.store;
  };

  const GraphCanvasProvider: FC<{ store: Store; children: ReactNode }> = ({ store, children }) => {
    const value = useMemo(() => ({ store }), [store]);
    return <Context.Provider value={value}>{children}</Context.Provider>;
  };

  const useGraphCanvasGraph = (): GraphSnapshot<TData> => {
    const store = useStore();
    const subscribe = useCallback((listener: () => void) => store.subscribe('graph', listener), [store]);
    return useSyncExternalStore(subscribe, () => store.getGraph());
  };

  const useGraphCanvasMeta = (): Readonly<TMeta> | null => {
    const store = useStore();
    const subscribe = useCallback((listener: () => void) => store.subscribe('meta', listener), [store]);
    return useSyncExternalStore(subscribe, () => store.getMeta());
  };

  const useGraphCanvasLoadStatus = (): LoadStatus => {
    const store = useStore();
    const subscribe = useCallback((listener: () => void) => store.subscribe('load', listener), [store]);
    return useSyncExternalStore(subscribe, () => store.getLoadStatus());
  };

  return {
    GraphCanvasProvider,
    useGraphCanvasGraph,
    useGraphCanvasMeta,
    useGraphCanvasLoadStatus,
  };
};
